import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	Ref,
	RefObject,
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import {
	HubDialogContext,
	HubDialogContextData,
	ViewSpecificHandlers
} from "./common";
import {
	HubDataSource,
	HubItemCategoryModel,
	HubItemDataSource,
	listCategories
} from "client/Api/Hub/hub";
import { isNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { Details as AnnouncementDetails } from "./Announcements/Details/Details";
import { Details as EventDetails } from "./Events/Details/Details";
import { Details as KnowledgeItemDetails } from "./KnowledgeItems/Details/Details";
import styles from "./HubDetailsDialog.module.less";
import { HubItemViewLocation } from "client/Api/Hub/common.details";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import { getHubUrlForEvent } from "./Events/EventsPage";
import { getHubUrlForKnowledgeItem, KnowledgeItemViewIdentifier } from "./KnowledgeItems/KnowledgeItemsPage";
import { getHubUrlForAnnouncement } from "./Announcements/AnnouncementsPage";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { KnowledgeItemClickableBreadcrumbSegment } from "./KnowledgeItems/useKnowledgeItemBreadcrumb";
import { hubItemKindToHubDataSourceMap } from "./Sidebar/common";

export type HubDetailsDialogContentProps = {
	id: number;
	itemKind: HubItemKind;
	commentId?: number;
	onIsBusyChange(isBusy: boolean): void;
	viewLocation: HubItemViewLocation;
	onLoadingError?: (error: unknown) => TryResult<void>;
};

export type HubDetailsDialogContentHandle = {
	beforeCloseHandler(): Promise<boolean>;
};

export const HubDetailsDialogContent = forwardRef(
	(props: HubDetailsDialogContentProps, ref: Ref<HubDetailsDialogContentHandle>): ReactElement => {
		const {
			id,
			onIsBusyChange: onIsBusyChangeFromProps,
			viewLocation,
			onLoadingError
		} = props;

		const [categories, setCategories] = useState<Map<number, HubItemCategoryModel>>(new Map());

		const viewSpecificHandlersRef: RefObject<ViewSpecificHandlers | undefined> = useRef<ViewSpecificHandlers>(undefined);

		const onIsBusyChange: typeof onIsBusyChangeFromProps = useStableImperativeCallback(onIsBusyChangeFromProps);
		const alertManager: AlertManager = useAlerts();
		const globalCancellation: AbortSignal = useUnmountedCancellation();

		const fetchCategories = useCallback(
			async (): Promise<void> => {
				onIsBusyChange(true);

				try {
					const categories: readonly HubItemCategoryModel[] = await listCategories(
						undefined,
						globalCancellation
					);

					setCategories(
						fromArray(
							categories,
							(x: HubItemCategoryModel): number => x.id
						)
					);
					onIsBusyChange(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onIsBusyChange(false);
				}
			},
			[
				alertManager,
				globalCancellation,
				onIsBusyChange
			]
		);

		useEffect(
			(): void => {
				fetchCategories();
			},
			[fetchCategories]
		);

		const getCategory = useCallback(
			(categoryId: number | nil): HubItemCategoryModel | undefined => {
				if (isNil(categoryId)) {
					return undefined;
				}

				return categories.get(categoryId);
			},
			[categories]
		);

		const selectCategoryHandler = useCallback(
			(categoryId: number): void => {
				const source: HubItemDataSource = hubItemKindToHubDataSourceMap[props.itemKind];
				let url: string;

				switch (source) {
					case HubDataSource.KnowledgeItem:
						url = getHubUrlForKnowledgeItem({
							source: HubDataSource.KnowledgeItem,
							viewIdentifier: {
								kind: "categoryId",
								categoryId
							}
						});
						break;
					case HubDataSource.Announcement:
						url = getHubUrlForAnnouncement({
							source: HubDataSource.Announcement,
							categoryId,
							itemId: undefined
						});
						break;
					case HubDataSource.Event:
						url = getHubUrlForEvent({
							source: HubDataSource.Event,
							categoryId,
							itemId: undefined
						});
						break;
					default:
						never(source);
				}

				navigateTo(url);
			},
			[props.itemKind]
		);

		const knowledgeItemBreadcrumbNavigationHandler = useCallback(
			(segment: KnowledgeItemClickableBreadcrumbSegment): void => {
				let viewIdentifier: KnowledgeItemViewIdentifier | undefined;

				switch(segment.kind) {
					case "overview":
						// No identifier
						break;
					case "group":
						viewIdentifier = {
							kind: "groupId",
							groupId: segment.id
						};
						break;
					case "category":
						viewIdentifier = {
							kind: "categoryId",
							categoryId: segment.id
						};
						break;
					default:
						never(segment);
				}

				navigateTo(getHubUrlForKnowledgeItem({
					source: HubDataSource.KnowledgeItem,
					viewIdentifier
				}));
			},
			[]
		);

		const hubDialogContext = useMemo(
			(): HubDialogContextData => {
				return {
					setViewSpecificHandlers: (options): void => {
						viewSpecificHandlersRef.current = options;
					}
				};
			},
			[]
		);

		const renderContent = (): ReactElement | null => {
			if (isNil(categories)) {
				return null;
			}

			switch (props.itemKind) {
				case HubItemKind.Announcement:
					return (
						<AnnouncementDetails
							mode="details-only"
							announcementId={id}
							getCategory={getCategory}
							deepLink={props.commentId
								? {
									kind: "comment",
									commentId: props.commentId
								}
								: undefined
							}
							viewLocation={viewLocation}
							onLoadingError={onLoadingError}
							onSelectCategory={selectCategoryHandler}
						/>
					);
				case HubItemKind.Event:
					return (
						<EventDetails
							mode="details-only"
							id={id}
							viewLocation={viewLocation}
							onLoadingError={onLoadingError}
							getCategory={getCategory}
							onSelectCategory={selectCategoryHandler}
							deepLink={props.commentId
								? {
									kind: "comment",
									commentId: props.commentId
								}
								: undefined
							}
						/>
					);
				case HubItemKind.KnowledgeItem:
					if (viewLocation === HubItemViewLocation.PopupQueue) {
						throw new Error("Knowledge items cannot be viewed in the popup queue");
					}

					return (
						<KnowledgeItemDetails
							mode="details-only"
							id={id}
							getCategory={getCategory}
							onSelectCategory={selectCategoryHandler}
							onBreadcrumbNavigation={knowledgeItemBreadcrumbNavigationHandler}
							viewLocation={viewLocation}
							deepLink={props.commentId
								? {
									kind: "comment",
									commentId: props.commentId
								}
								: undefined
							}
						/>
					);
				default:
					never(props.itemKind);
			}
		};

		useImperativeHandle(
			ref,
			(): HubDetailsDialogContentHandle => ({
				async beforeCloseHandler(): Promise<boolean> {
					return await viewSpecificHandlersRef.current
						?.beforeCloseHandler?.()
						?? false;
				}
			}),
			[]
		);

		return (
			<div className={styles.body}>
				<HubDialogContext.Provider value={hubDialogContext}>
					{renderContent()}
				</HubDialogContext.Provider>
			</div>
		);
	}
);

HubDetailsDialogContent.displayName = "HubDetailsDialogContent";